import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const InventoryHistory = ({ product, onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, stock_in, stock_out, transfer, adjustment

  useEffect(() => {
    loadTransactions();
  }, [product, filter]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (filter !== 'all') {
        params.transaction_type = filter;
      }

      const url = product 
        ? `/api/inventory/transactions/${product.id}`
        : '/api/inventory/transactions';

      const response = await axios.get(url, { params });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      toast.error('שגיאה בטעינת ההיסטוריה');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionBadge = (type) => {
    const badges = {
      stock_in: { label: 'קבלה', color: 'bg-green-100 text-green-800' },
      stock_out: { label: 'יציאה', color: 'bg-red-100 text-red-800' },
      transfer: { label: 'העברה', color: 'bg-blue-100 text-blue-800' },
      adjustment: { label: 'התאמה', color: 'bg-yellow-100 text-yellow-800' }
    };
    return badges[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  };

  const getWarehouseLabel = (warehouse) => {
    const labels = {
      b2c: '🛒 B2C',
      b2b: '🏢 B2B',
      external: '📦 ספק',
      customer: '👤 לקוח'
    };
    return labels[warehouse] || warehouse;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div>
            <h2 className="text-xl font-bold text-gray-900">היסטוריית תנועות מלאי</h2>
            {product && (
              <p className="text-sm text-gray-600 mt-1">
                {product.name} ({product.sku})
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Filter */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <div className="flex gap-2 flex-wrap">
              {['all', 'stock_in', 'stock_out', 'transfer', 'adjustment'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type === 'all' && 'הכל'}
                  {type === 'stock_in' && 'קבלות'}
                  {type === 'stock_out' && 'יציאות'}
                  {type === 'transfer' && 'העברות'}
                  {type === 'adjustment' && 'התאמות'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              אין תנועות להצגה
            </div>
          ) : (
            <div className="space-y-4">
              {transactions.map((transaction) => {
                const badge = getTransactionBadge(transaction.transaction_type);
                
                return (
                  <div key={transaction.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                            {badge.label}
                          </span>
                          <span className="text-sm text-gray-600">
                            {formatDate(transaction.created_at)}
                          </span>
                        </div>

                        {/* Product Info */}
                        {transaction.product && (
                          <div className="text-sm font-medium text-gray-900 mb-2">
                            {transaction.product.name} ({transaction.product.sku})
                          </div>
                        )}

                        {/* Transfer Info */}
                        <div className="flex items-center gap-4 text-sm mb-2">
                          {transaction.from_warehouse && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">מ:</span>
                              <span className="font-medium">{getWarehouseLabel(transaction.from_warehouse)}</span>
                            </div>
                          )}
                          
                          {transaction.from_warehouse && transaction.to_warehouse && (
                            <span className="text-gray-400">→</span>
                          )}
                          
                          {transaction.to_warehouse && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">ל:</span>
                              <span className="font-medium">{getWarehouseLabel(transaction.to_warehouse)}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">כמות:</span>
                            <span className={`font-bold ${
                              transaction.transaction_type === 'stock_out' ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {transaction.transaction_type === 'stock_out' ? '-' : '+'}
                              {transaction.quantity}
                            </span>
                          </div>
                        </div>

                        {/* Stock After */}
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                          <span>אחרי: B2C: {transaction.stock_b2c_after} | B2B: {transaction.stock_b2b_after}</span>
                        </div>

                        {/* Performer */}
                        {transaction.performer && (
                          <div className="text-xs text-gray-600">
                            בוצע ע"י: {transaction.performer.first_name} {transaction.performer.last_name}
                          </div>
                        )}

                        {/* Notes */}
                        {transaction.notes && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                            💬 {transaction.notes}
                          </div>
                        )}

                        {/* Reference */}
                        {transaction.reference_type && transaction.reference_id && (
                          <div className="mt-2 text-xs text-gray-500">
                            קשור ל: {transaction.reference_type} ({transaction.reference_id.substring(0, 8)}...)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryHistory;
